// @ts-nocheck
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send } from "lucide-react";

interface Props {
  eventId: string;
  /** Absolute URL to the coach's roster / verification view. Defaults to the event page. */
  verifyUrl?: string;
  variant?: string;
  size?: string;
  className?: string;
  label?: string;
}

export default function SendVerificationReminderButton({
  eventId,
  verifyUrl,
  variant = "outline",
  size = "sm",
  className = "",
  label = "Send verification reminder",
}: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(false);
  const [previewOnly, setPreviewOnly] = useState(false);
  const [sending, setSending] = useState(false);

  const channels = [email && "email", sms && "sms"].filter(Boolean) as string[];

  const resolvedUrl =
    verifyUrl ||
    `${window.location.origin}/club-coach-dashboard/events/${eventId}/roster-management`;

  const handleSend = async () => {
    if (channels.length === 0) {
      toast.error("Pick at least one channel");
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("verification-reminder-send", {
        body: {
          event_id: eventId,
          channels,
          verify_url: resolvedUrl,
          dry_run: previewOnly,
        },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error || "Send failed");

      if (data?.dry_run) {
        toast.success(`Preview: ${data.recipients ?? 0} recipients would be messaged`);
      } else {
        toast.success(
          `${data?.recipients ?? 0} recipients · ${data?.sent ?? 0} sent · ${data?.failed ?? 0} failed`,
        );
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.message || "Failed to send verification reminder");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => setOpen(true)}>
        <Send className="h-4 w-4 mr-2" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send verification reminder</DialogTitle>
            <DialogDescription>
              Sends the "Player Verification Reminder" template (editable in Settings → Messaging)
              to every coach with unverified players and to the unverified athletes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Channels</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} />
                  <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={sms} onCheckedChange={(v) => setSms(!!v)} />
                  <span className="text-sm">SMS</span>
                </label>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={previewOnly} onCheckedChange={(v) => setPreviewOnly(!!v)} />
              <span className="text-sm">Preview only (don't actually send)</span>
            </label>

            <p className="text-xs text-muted-foreground break-all">
              Verification link: {resolvedUrl}
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? "Working…" : previewOnly ? "Preview" : "Send reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
